import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Avatar,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAgentReport } from '../../../../contexts/AgentReportContext';
import { useNavigate } from 'react-router-dom';

const Tasks = ({ agentId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { fetchSingleAgentTasks } = useAgentReport();
  const [tasksData, setTasksData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({
    total_count: 0,
    current_page: 1,
    page_size: 25,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });

  useEffect(() => {
    if (agentId) {
      fetchTasks();
    }
  }, [agentId, currentPage, pageSize, searchQuery]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await fetchSingleAgentTasks(agentId, currentPage, pageSize, searchQuery);
      
      if (response && response.tasks) {
        setTasksData(response.tasks);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setTasksData([]);
        setPagination({
          total_count: 0,
          current_page: currentPage,
          page_size: pageSize,
          total_pages: 1,
          has_next: false,
          has_previous: false
        });
      }
      setError(null);
    } catch (error) {
      setError(error);
      setTasksData([]);
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'upcoming':
        return 'info';
      case 'pending':
        return 'warning';
      case 'backlog':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'upcoming':
        return <ScheduleIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'backlog':
        return <PendingIcon fontSize="small" />;
      default:
        return <AssignmentIcon fontSize="small" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle search with debounce
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100%',
      flex: 1,
      minHeight: '70vh'
    }}>
      <Paper sx={{ 
        borderRadius: 3, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        height: '100%'
      }}>
        <Box sx={{ pt: 2, px: 3, pb: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Tasks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track and manage agent tasks and activities
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={pageSize}
                  label="Per Page"
                  onChange={handlePageSizeChange}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {pagination.total_count > 0 ? (
                <>Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, pagination.total_count)} of {pagination.total_count} tasks</>
              ) : (
                'No tasks found'
              )}
            </Typography>
            {searchQuery && (
              <Typography variant="body2" color="primary">
                Filtered by: "{searchQuery}"
              </Typography>
            )}
          </Box>
        </Box>
        
        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">Error loading tasks: {error.message}</Typography>
          </Box>
        )}
        
        <TableContainer sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ height: '56px' }}>
                <TableCell sx={{ fontWeight: 600, py: 2, width: '40%' }}>Task</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, width: '15%' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, width: '12%' }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, width: '18%' }}>Scheduled Date</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2, width: '15%' }}>Completed Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && tasksData && tasksData.length > 0 ? (
                tasksData.map((task) => (
                  <TableRow 
                    key={task.id} 
                    hover 
                    sx={{ 
                      height: '56px',
                      ...(task.status === 'backlog' && {
                        bgcolor: alpha(theme.palette.warning.main, 0.06),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                        },
                      }),
                      ...(task.status === 'pending' && {
                        bgcolor: alpha(theme.palette.warning.main, 0.06),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                        },
                      }),
                      ...(task.status === 'completed' && {
                        bgcolor: alpha(theme.palette.success.main, 0.06),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                        },
                      }),
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ 
                          width: 36,
                          height: 36,
                          bgcolor: alpha(
                            theme.palette[getStatusColor(task.status)].main,
                            0.1
                          ),
                          color: theme.palette[getStatusColor(task.status)].main
                        }}>
                          {getStatusIcon(task.status)}
                        </Avatar>
                        <Box>
                          {task.lead?.id ? (
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/leads/${task.lead.id}`);
                              }}
                            >
                              {task.title}
                            </Typography>
                          ) : (
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {task.title}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {task.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        icon={getStatusIcon(task.status)}
                        label={task.status_verbose || task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                        color={getStatusColor(task.status)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={task.priority_verbose || task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                        color={getPriorityColor(task.priority)}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {task.assignedDateFormatted || formatDate(task.assignedDate)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {task.completedDateFormatted || task.completedDate ? (
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                          {task.completedDateFormatted || formatDate(task.completedDate)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : !loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tasks found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination Controls */}
        {!loading && pagination.total_pages > 1 && (
          <Box sx={{ 
            p: 3, 
            borderTop: 1, 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Pagination
              count={pagination.total_pages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        
        {/* Loading overlay */}
        {loading && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1
          }}>
            <CircularProgress />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Tasks;


